import {useContext, useEffect, useState} from "react";
import {LAKE_CHART_MODES} from "../utils/ChartFormatUtils";
import SensorService from "../API/SensorService";
import {
    getDateByTimestamp,
    getMinusMonthTimestamp,
    getMinusWeekTimestamp,
    getMinusYearTimestamp,
    getTodayEndTimestamp,
    getWeekDayName,
    INTERVALS,
    FIRST_TIMESTAMP,
    formatTooltipDate,
    getEndOfDay,
    formatDayNumberWithMonthName,
    getEachExactDayNumberDate,
    DATE_CONSTANTS
} from "../utils/DateUtils";
import {CommonContext} from "../context";

export function useChartData() {
    const {isAuth, setIsSensorInMaintenance} = useContext(CommonContext);
    const [activeMode, setActiveMode] = useState(LAKE_CHART_MODES.week);

    const [weekData, setWeekData] = useState(); //labels, temperatures, waterLevels
    const [monthData, setMonthData] = useState();
    const [yearData, setYearData] = useState();
    const [maxData, setMaxData] = useState();

    const todayTimestamp = getTodayEndTimestamp()

    useEffect(() => {
        if (isAuth) {
            fetchData();
        }
    }, [activeMode, isAuth]);

    function fetchData() {
        if (!weekData) {
            fetchWeekData();
        }
        if (!monthData) {
            fetchMonthData();
        }
        if (!yearData) {
            fetchYearData();
        }
        if (!maxData) {
            fetchMaxData();
        }
    }

    function fetchWeekData() {
        const minusWeekTimestamp = getMinusWeekTimestamp();
        SensorService.fetchTemperature(minusWeekTimestamp, todayTimestamp, INTERVALS.halfDay)
            .then(response=> {
                const filteredData = filterResponse(response);
                filteredData.shift(); //removing first element, so first day of chart didn't have 3 values as server returns

                const labels = [];
                filteredData.forEach(element => labels.push(toLabelObject(
                    labels.length % 2 === 0 ? getWeekDayName(element.timestamp) : null, //there is 2 elements per day, see INTERVALS.halfDay
                        formatTooltipDate(getDateByTimestamp(element.timestamp))
                )));
                const temperatures = filteredData.map(element => element.value);

                setWeekData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }));
            });
        SensorService.fetchWaterLevel(minusWeekTimestamp, todayTimestamp, INTERVALS.halfDay)
            .then(response => {
                const filteredData = filterResponse(response);
                filteredData.shift();

                setWeekData(prev => ({
                    ...prev,
                    waterLevels: filteredData.map(element => element.value)
                }));
            })
    }

    function defineIsSensorInMaintenance(data) {
        if (!data || data.length === 0) {
            setIsSensorInMaintenance(true);
            return;
        }

        const newestTimestamp = data[data.length - 1].timestamp;
        const daysDifference = (todayTimestamp - newestTimestamp) / DATE_CONSTANTS.secondsInDay;
        if (daysDifference > 1) {
            setIsSensorInMaintenance(true);
        }
    }

    function fetchMonthData() {
        const minusMonthTimestamp = getMinusMonthTimestamp();
        const eachNDay = 5;
        SensorService.fetchTemperature(minusMonthTimestamp, todayTimestamp, INTERVALS.day)
            .then(response=> {
                const filteredData = filterResponse(response);
                defineIsSensorInMaintenance(filteredData);

                const labels = filteredData.map(element => toLabelObject(
                    getEachNDayNumber(getDateByTimestamp(element.timestamp), eachNDay),
                    formatTooltipDate(getDateByTimestamp(element.timestamp))
                ));
                const temperatures = filteredData.map(element => element.value);

                const earliestDate = findEarliestDate(filteredData);
                for (let i = 0; i < 31 - filteredData.length; i++) {
                    earliestDate.setDate(earliestDate.getDate() - 1)
                    labels.unshift(toLabelObject(
                        getEachNDayNumber(earliestDate, eachNDay),
                        ''
                    ));
                    temperatures.unshift(null);
                }

                setMonthData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }));
            });
        SensorService.fetchWaterLevel(minusMonthTimestamp, todayTimestamp, INTERVALS.day)
            .then(response => {
                const filteredData = filterResponse(response);

                const waterLevels = filteredData.map(element => element.value);
                for (let i = 0; i < 31 - filteredData.length; i++) {
                    waterLevels.unshift(null);
                }

                setMonthData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }));
            });

        const getEachNDayNumber = (date, n) => {
            return date.getDate() % n === 0
                ? formatDayNumberWithMonthName(date)
                : null;
        };
    }

    function fetchYearData() {
        const minusYearTimestamp = getMinusYearTimestamp();
        const fullYearElementsNumber = 365; //used to fill chart if there is not enough data for last 365 days
        SensorService.fetchTemperature(minusYearTimestamp, todayTimestamp, INTERVALS.day)
            .then(response=> {
                const filteredData = filterResponse(response);
                const newestDayNumber = findNewestDayNumber(filteredData);

                const labels = filteredData.map(element => {
                    const date = getDateByTimestamp(element.timestamp);
                    return toLabelObject(
                        getEachExactDayNumberDate(date, newestDayNumber),
                        formatTooltipDate(date)
                    );
                });
                const temperatures = filteredData.map(element => element.value);

                const earliestDate = findEarliestDate(filteredData);
                for (let i = 0; i < fullYearElementsNumber - filteredData.length; i++) {
                    const daysToMinusLabel = 3 + (i % 2);
                    earliestDate.setDate(earliestDate.getDate() - daysToMinusLabel);
                    labels.unshift(toLabelObject(
                        getEachExactDayNumberDate(earliestDate, newestDayNumber),
                        ''
                    ));
                    temperatures.unshift(null);
                }

                setYearData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }));
            });
        SensorService.fetchWaterLevel(minusYearTimestamp, todayTimestamp, INTERVALS.day)
            .then(response => {
                const filteredData = filterResponse(response);
                const waterLevels = filteredData.map(element => element.value);
                for (let i = 0; i < fullYearElementsNumber - filteredData.length; i++) {
                    waterLevels.unshift(null);
                }

                setYearData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }));
            });
    }

    function fetchMaxData() {
        SensorService.fetchTemperature(FIRST_TIMESTAMP, todayTimestamp, INTERVALS.day)
            .then(response=> {
                const filteredData = filterResponse(response);
                const newestDayNumber = findNewestDayNumber(filteredData);
                const labels = filteredData.map(element => {
                    const date = getDateByTimestamp(element.timestamp);
                    return toLabelObject(
                        getEachExactDayNumberDate(date, newestDayNumber),
                        formatTooltipDate(date)
                    );
                });
                const temperatures = filteredData.map(element => element.value);

                setMaxData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }));
            });
        SensorService.fetchWaterLevel(FIRST_TIMESTAMP, todayTimestamp, INTERVALS.day)
            .then(response => {
                const filteredData = filterResponse(response);
                const waterLevels = filteredData.map(element => element.value);

                setMaxData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }));
            });
    }

    function filterResponse(response) {
        return response.data.filter(element => element.value !== 0);
    }

    function toLabelObject(axisValue, tooltipValue) {
        return JSON.stringify({
            axisValue: axisValue,
            tooltipValue: tooltipValue
        })
    }

    function findEarliestDate(responseData) {
        return responseData.length > 0
            ? getDateByTimestamp(responseData[0].timestamp)
            : getEndOfDay();
    }

    function findNewestDayNumber(responseData) {
        return responseData.length > 0
            ? getDateByTimestamp(responseData[responseData.length - 1].timestamp).getDate()
            : getEndOfDay().getDate();
    }

    let activeData;
    let activeModeStartTimestamp;
    switch (activeMode) {
        case LAKE_CHART_MODES.week:
            activeData = weekData;
            activeModeStartTimestamp = getMinusWeekTimestamp();
            break;
        case LAKE_CHART_MODES.month:
            activeData = monthData;
            activeModeStartTimestamp = getMinusMonthTimestamp();
            break;
        case LAKE_CHART_MODES.year:
            activeData = yearData;
            activeModeStartTimestamp = getMinusYearTimestamp();
            break;
        case LAKE_CHART_MODES.max:
            activeData = maxData;
            activeModeStartTimestamp = FIRST_TIMESTAMP;
            break;
        default:
            activeData = weekData;
            activeModeStartTimestamp = getMinusWeekTimestamp();
            break;
    }

    function downloadXlsxData() {
        SensorService.downloadXlsxData(activeModeStartTimestamp, todayTimestamp);
    }

    return [activeMode, setActiveMode, activeData, downloadXlsxData];
}